import { useCallback, useMemo, useRef, useState } from 'react'
import { ToastContext } from './toastContext'

const VARIANT_STYLES = {
  success: 'border-emerald-200 bg-white/90 text-emerald-800',
  error: 'border-red-200 bg-white/90 text-red-700',
  info: 'border-slate-200 bg-white/90 text-slate-700',
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const timeouts = useRef(new Map())

  const dismissToast = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
    const timeout = timeouts.current.get(id)
    if (timeout) {
      clearTimeout(timeout)
      timeouts.current.delete(id)
    }
  }, [])

  const showToast = useCallback((toast) => {
    const id = toast.id ?? (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`)
    const variant = toast.variant ?? 'info'
    const duration = Number.isFinite(toast.duration) ? Number(toast.duration) : 6000

    setToasts((current) => [
      ...current,
      {
        id,
        title: toast.title ?? '',
        description: toast.description ?? '',
        variant,
      },
    ])

    const timeout = setTimeout(() => {
      dismissToast(id)
    }, Math.max(duration, 2000))

    timeouts.current.set(id, timeout)

    return id
  }, [dismissToast])

  const contextValue = useMemo(
    () => ({
      showToast,
      dismissToast,
    }),
    [showToast, dismissToast],
  )

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className="pointer-events-none fixed inset-x-4 top-4 z-[100] flex flex-col items-end gap-3 sm:inset-x-auto sm:right-4">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`pointer-events-auto w-full max-w-sm rounded-2xl border px-4 py-3 shadow-xl shadow-black/5 backdrop-blur ${VARIANT_STYLES[toast.variant] ?? VARIANT_STYLES.info}`}
            role="status"
            aria-live="polite"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                {toast.title ? <p className="text-sm font-semibold">{toast.title}</p> : null}
                {toast.description ? <p className="text-sm leading-5 text-slate-600">{toast.description}</p> : null}
              </div>
              <button
                type="button"
                onClick={() => dismissToast(toast.id)}
                className="rounded-full border border-transparent p-1 text-xs font-semibold text-slate-400 transition hover:border-slate-200 hover:text-slate-600"
              >
                Schließen
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

